import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import Dropdown from 'react-bootstrap/Dropdown'

import useAuthentication from '../../hooks/useAuthentication';
import useUserManagement from '../../hooks/useUserManagement';
import useCadmObject from '../../hooks/useCadmObject';
import useConfiguration from '../../hooks/useConfiguration';

import logo from '../../gncspace_logo.png'

const NavHeader = ({ user }) => {
    const { logout } = useAuthentication();
    const { currentUser } = useAuthentication();
    const { siteName } = useConfiguration()

    const disable2fa = currentUser?.is_2fa_required ? true : false
    return (
        <div className="sidebar-header">
            <div className="site-logo">
                <Link to="/main" data-tooltip-place="right" data-tooltip-hidden="true">
                    <img src={logo} alt="GNC"style={{ width: '100%', paddingBottom: '40px' }} />
                </Link>
            </div>
            <div className="profile-name">{user.label}</div>
            <Dropdown>
                <Dropdown.Toggle size="sm" variant="info" className="profile-toggle">
                    Account
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    <Dropdown.Item as={Link} to="/account/2fa" disabled={disable2fa}><i className="fa fa-shield" style={{ marginRight: '4px' }}></i>2FA Settings</Dropdown.Item>
                    <Dropdown.Item as={Link} to="/account/update_password"><i className="fa fa-lock" style={{ marginRight: '4px' }}></i>Change password</Dropdown.Item>
                    <Dropdown.Item as={Link} onClick={logout}><i className="fa fa-sign-out" style={{ marginRight: '4px' }}></i>Log out</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>
            <div className="logo-element">
                {siteName}
            </div>
        </div>
    )
}

const NavigationLinkTitle = ({ label }) => (
    <li>
        <div className="nav-section-label">
            {label}
        </div>
    </li>
)

const NavigationLink = ({ to, label, className, icon }) => (
    <li className={className}>
        <Link to={to} data-tooltip-id="gnctooltip" data-tooltip-content={label} data-tooltip-place="right" data-tooltip-hidden="true" data-tip-disable="false">
            <i className={`fa ${icon}`}></i>
            <span className="nav-label">{label}</span> 
        </Link>
    </li>
)

const Navigation = () => {
    const project = useSelector(state => state.pscState.value).project
    const location = useLocation()

    const { currentUser } = useAuthentication();
    const { featureSharepoint } = useConfiguration()

    const {
        userHasRole,
        userHasProjectAccess,
        userHasProjectAccessLevel,
        userHasExplicitProjectAccess,
        userHasProjectInformationAccess,
        userShowNavigation,
    } = useUserManagement()
    
    const {
        cadmObjects,
    } = useCadmObject('all')

    const activeRoute = (routeName) => {
        return location.pathname.indexOf(routeName) > -1 ? 'active' : '';
    }

    const activeExactRoute = (routeName) => {
        return location.pathname === routeName ? 'active' : '';
    }

    if (! userShowNavigation(currentUser)) {
        return <div/>
    }

    const hasProjectItems = project?.id && project?.project_type?.items && cadmObjects
    const hasProjectLinks = hasProjectItems && (userHasProjectInformationAccess(currentUser, project?.id, project?.project_type?.name) || userHasProjectAccess(currentUser, project?.id))

    const userHasRoleSerialNumbersReadonly = userHasRole(currentUser, 'serial_numbers_readonly')
    const userHasRoleQMSDocumentsReadonly = userHasRole(currentUser, 'qms_documents_readonly')
    const userHasRoleQaDocumentsReadonly = userHasRole(currentUser, 'qa_documents_readonly')
    const userHasRoleQaRecordsReadonly = userHasRole(currentUser, 'qa_records_readonly')
    const hasQualityLinks = userHasRoleSerialNumbersReadonly ||
                            userHasRoleQMSDocumentsReadonly ||
                            userHasRoleQaDocumentsReadonly ||
                            userHasRoleQaRecordsReadonly

    const userHasRoleUserAdmin = userHasRole(currentUser, 'user_admin')
    const userHasRoleCadmAdmin = userHasRole(currentUser, 'cadm_admin')
    const hasAdminLinks = userHasRoleUserAdmin || userHasRoleCadmAdmin

    const userIsCadmController = userHasProjectAccessLevel(currentUser, project?.id, "CADM_CONTROLLER") ? true : false

    return (
        <nav className="navbar sidebar" role="navigation">
            <NavHeader user={currentUser} />

            {/* <!-- Project / Product --> */}
            <ul className="nav" id="side-menu">
                <NavigationLink to="/main" className={activeRoute('/main')} label="Home" icon="fa-th-large" />
                <NavigationLink to="/portfolio" className={activeExactRoute('/portfolio')} label="Portfolio" icon="fa-tasks" />

                {hasProjectLinks &&
                    <>
                        <NavigationLinkTitle label={project?.project_type?.name} />
                        <NavigationLink to={`/portfolio/${project.id}/dashboard`} className={activeRoute(`/portfolio/${project.id}/dashboard`)} label="Dashboard" icon="fa-tachometer" />
                        <NavigationLink to={`/portfolio/${project.id}/information`} className={activeRoute(`/portfolio/${project.id}/information`)} label="Information" icon="fa-info-circle" />
                    </>
                }
                {hasProjectItems && userHasProjectAccess(currentUser, project?.id) &&
                    project.project_type.items.map(item => (
                        <NavigationLink key={item.name} to={item.href.replace('$project_id', project.id)} className={activeRoute(`${item.href.replace('$project_id', project.id)}`)} label={item.name} icon={`${item.fa_icon}`} />
                    ))
                }
            
                {/* Bonus link! */}
                {userIsCadmController && featureSharepoint &&
                    <NavigationLink to={`/portfolio/${project.id}/integrations/sharepoint`} className={activeRoute(`/portfolio/${project.id}/integrations/sharepoint`)} label="SharePoint" icon="fa-windows" />
                }
            
                {/* <!-- Quality --> */}
                {hasQualityLinks &&
                    <NavigationLinkTitle label="QUALITY" />
                }
                {userHasRoleSerialNumbersReadonly &&
                    <NavigationLink to="/serial_numbers" className={activeRoute('/serial_numbers')} label="Serial Numbers" icon="fa-list-ul" />
                }
                {userHasRoleQMSDocumentsReadonly &&
                    <NavigationLink to="/qms_documents" className={activeRoute('/qms_documents')} label="QMS Documents" icon="fa-file-word-o" />
                }
                {userHasRoleQaDocumentsReadonly &&
                    <NavigationLink to="/qa_documents" className={activeRoute('/qa_documents')} label="QA Documents" icon="fa-file-word-o" />
                }
                {userHasRoleQaRecordsReadonly &&
                    <NavigationLink to="/qa_records" className={activeRoute('/qa_records')} label="QA Records" icon="fa-sticky-note-o" />
                }

                {/* <!-- Admin --> */}
                {hasAdminLinks &&
                    <NavigationLinkTitle label="ADMIN" />
                }
                {userHasRoleUserAdmin &&
                    <NavigationLink to="/users" className={activeRoute('/users')} label="Users" icon="fa-user" />
                }
                {userHasRoleUserAdmin &&
                 <NavigationLink to="/usergroups" className={activeRoute('/usergroups')} label="User Groups" icon="fa-users" />
                }
                {userHasRoleCadmAdmin &&
                    <NavigationLink to="/cadm" className={activeRoute('/cadm')} label="CADM" icon="fa-wrench" />
                }
            </ul>
        </nav>
    )
}

export default Navigation
